import Cta from "@components/Cta/Cta";
import { TypeSiteHeaderFields } from "@customTypes/contentful";
import { HEADER_ANCHOR } from "@utilities/e2e/e2eAnchorsList";
import { getE2EAnchorAttribute } from "@utilities/e2e/getE2EAnchor";
import clsx from "clsx";
import { useRouter } from "next/router";
import { FC, memo, useEffect, useRef, useState } from "react";
import ArrowSvg from "../../public/image/svg/arrow.svg";
import CloseSvg from "../../public/image/svg/cross.svg";
import styles from "./C01.2_HeaderSearch.module.scss";

type Props = {
  content: Partial<TypeSiteHeaderFields>;
  show: boolean;
  close: () => void;
};

export const HeaderSearch: FC<Props> = (props) => {
  const { content, show, close } = props;
  const {
    areaOfInterestLinks,
    areaOfInterestText,
    searchHelpText,
    searchPlaceholderText,
    searchResultsUrl,
  } = content;

  const modalRef = useRef<HTMLDialogElement>(null);
  const router = useRouter();
  const [searchInput, setSearchInput] = useState("");
  const search = () => {
    if (router.asPath.includes("/search")) {
      router
        .push(`/${searchResultsUrl?.url}#q=${searchInput}`)
        .then(() => router.reload());
    } else {
      router.push(`/${searchResultsUrl?.url}#q=${searchInput}`);
    }
  };

  useEffect(() => {
    close();
  }, [router]);

  const handleInputKeypress = (e) => {
    if (e.code === "Enter") {
      search();
    }
  };

  const handleClose = () => {
    close();
  };

  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement) return;

    if (show) {
      modalElement?.showModal?.();
    } else {
      modalElement?.close?.();
    }
  }, [show]);

  return (
    <dialog
      onCancel={(event) => {
        event.preventDefault();
        handleClose?.();
      }}
      ref={modalRef}
      className={clsx(styles.search, show && styles.show)}
      aria-modal
      aria-hidden={!show}
    >
      <div className={styles.inside}>
        <p className={styles.helper} id="search-label">
          {searchHelpText}
        </p>
        <div className={styles.input}>
          <input
            type="text"
            placeholder={searchPlaceholderText}
            aria-describedby="search-label"
            name="searchTerm"
            autoComplete="off"
            aria-label="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleInputKeypress}
          />
          <button
            onClick={search}
            type="submit"
            className="icon-arrow"
            aria-label="send"
            data-grunticon-embed
          >
            <ArrowSvg />
          </button>
        </div>

        <div className={styles.links}>
          <p className={styles.helper}>{areaOfInterestText}</p>
          {areaOfInterestLinks && (
            <ul>
              {areaOfInterestLinks?.map((link, index) => (
                <li key={`${link.linkText}${index}`}>
                  <Cta
                    type="tertiary"
                    icon="icon-arrow"
                    url={link.url}
                    linkText={link.linkText}
                    light
                    preventDefault={false}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <button
        className={styles.close}
        tabIndex={0}
        aria-label="Close search"
        onClick={handleClose}
        {...getE2EAnchorAttribute(HEADER_ANCHOR, "close-search-btn")}
      >
        <CloseSvg />
      </button>
    </dialog>
  );
};

export default memo(HeaderSearch);
